/**
 * LeadNotesPanel - Notes tab panel (add / edit / delete notes) for a lead.
 * Extracted verbatim from LeadDetailPage.jsx (behavior unchanged); state/handlers passed as props.
 */
import React from 'react';
import { parseISO, formatDistanceToNow } from 'date-fns';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Plus, FileText, Loader2, Edit, Trash2 } from 'lucide-react';

export default function LeadNotesPanel({
  notesHook,
  notes,
  editingNoteId,
  setEditingNoteId,
  editNoteContent,
  setEditNoteContent,
  savingEditNote,
  setSavingEditNote,
}) {
  return (
    <>
                <div className="flex gap-2">
                  <Textarea placeholder="Add a note..." value={notesHook.newNote} onChange={(e) => notesHook.setNewNote(e.target.value)} rows={3} className="flex-1" data-testid="note-input" />
                  <Button onClick={notesHook.handleAddNote} disabled={notesHook.addingNote || !notesHook.newNote.trim()} data-testid="add-note-btn">
                    {notesHook.addingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
                {notes.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500">No notes yet. Add your first note above.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {notes.map(note => {
                      const isEditing = editingNoteId === note.id;
                      return (
                        <div key={note.id} className="p-3 border rounded-lg hover:border-indigo-200 transition-colors group" data-testid={`note-${note.id}`}>
                          {isEditing ? (
                            <div className="space-y-2">
                              <Textarea
                                value={editNoteContent}
                                onChange={(e) => setEditNoteContent(e.target.value)}
                                rows={3}
                                autoFocus
                                data-testid={`edit-note-input-${note.id}`}
                              />
                              <div className="flex items-center justify-end gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={savingEditNote}
                                  onClick={() => { setEditingNoteId(null); setEditNoteContent(''); }}
                                  data-testid={`cancel-edit-note-${note.id}`}
                                >
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  disabled={savingEditNote || !editNoteContent.trim()}
                                  onClick={async () => {
                                    setSavingEditNote(true);
                                    try {
                                      const ok = await notesHook.handleEditNote(note.id, editNoteContent);
                                      if (ok !== false) {
                                        setEditingNoteId(null);
                                        setEditNoteContent('');
                                      }
                                    } finally {
                                      setSavingEditNote(false);
                                    }
                                  }}
                                  data-testid={`save-edit-note-${note.id}`}
                                >
                                  {savingEditNote ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                                </Button>
                              </div>
                            </div>
                          ) : (
                            <>
                              <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                              <div className="flex items-center justify-between mt-2">
                                <p className="text-xs text-slate-500">
                                  {note.created_by_name} • {note.created_at ? formatDistanceToNow(parseISO(note.created_at), { addSuffix: true }) : ''}
                                  {note.updated_at && <span className="italic ml-1">(edited)</span>}
                                </p>
                                <div className="flex items-center gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-slate-500 hover:text-indigo-600"
                                    onClick={() => { setEditingNoteId(note.id); setEditNoteContent(note.content); }}
                                    data-testid={`edit-note-${note.id}`}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-600"
                                    onClick={() => notesHook.handleDeleteNote(note.id)}
                                    data-testid={`delete-note-${note.id}`}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

    </>
  );
}
